import type { DataPointExportDefinition, ExportType, Provider, ProviderFile, ProviderFileRef } from "fileParsing/types";
import type { ParsedExport } from "@store/data/types";
import { removeDuplicates } from "@utils/array";
import { toISO8601Date } from "@utils/format";
import { v4 } from "uuid";
import { deserializeOutputs } from "@/containers/Visuals/Sankey/parserProcessing";
import { getProvider } from "./definitions";
import { commonName } from "./groupFiles";
import { compileFn } from "./wrangler/compile";
import {
  countDataPoints,
  createDataPointTemplate,
  extractFromTypeSchema,
  findDataPaths,
} from "./wrangler/extractTemplates";
import { pathEquals, topLevel } from "./wrangler/path";
import { isSimilarData, mergeUnionTypes, removeSamples } from "./wrangler/schema";
import {
  type DataPointTemplate,
  type DataTemplate,
  EMPTY_SCHEMA,
  type Path,
  UserInteractionStatus,
  Visibility,
  type WranglerItem,
} from "./wrangler/types";

export const createExport = (
  providerItem: ParsedExport,
  providerFiles: ProviderFile[],
  wranglerItems: WranglerItem[],
) => {
  const provider = getProvider(providerItem.providerId);
  const instructions = write(wranglerItems);

  const dataExport: Provider = {
    id: `${providerItem.properties.providerName}`,
    version: provider.version + 1,
    name: providerItem.properties.providerName,
    author: providerItem.properties.author,
    date: toISO8601Date(new Date()),
    url: providerItem.properties.url,
    strings: {
      en: {
        name: provider.strings.en.name,
        help: providerItem.properties.helpEn,
      },
      de: {
        name: provider.strings.de.name,
        help: providerItem.properties.helpDe,
      },
    },
    // Export wrangle instructions
    instructions,
    fileStructure: removeDuplicates(providerFiles.map((file) => topLevel(file.path))),
    optionalFileStructure: [],
  };
  return JSON.stringify(dataExport);
};

export const write = (items: WranglerItem[]) => {
  return items.map<ExportType>((item) => ({
    schema: removeSamples(item.schema),
    templates: item.templates
      .filter(({ visibility }) => visibility === Visibility.visible)
      .map(
        ({
          path,
          category,
          subcategory,
          content,
          selectedTimestamps,
          selectedUris,
          selectedContacts,
          selectedLatitudes,
          selectedLongitudes,
        }) =>
          ({
            path,
            category,
            subcategory: subcategory
              .filter(({ type, value }) => type !== "text" || value !== "")
              .map((v) => ({
                type: v.type,
                value: v.value,
              })),
            content: content
              .filter(({ type, value }) => type !== "text" || value !== "")
              .map((v) => ({ type: v.type, value: v.value })),
            selectedTimestamps,
            selectedUris,
            selectedContacts,
            selectedLatitudes,
            selectedLongitudes,
          }) as DataPointExportDefinition,
      ),
    hidden: false,
  }));
};

type ChainType = ({ type: "text"; value: string } | { type: "data"; value: Path })[];

const restoreDataPointTemplate = (templateExport: DataPointExportDefinition): DataPointTemplate => {
  // Empty texts are not exported, thus they must be restored for the GUI.
  // Make sure that between each to data-DataTemplates is a text-DataTemplate.
  // Value must start and end with texts.
  const restoreDataTemplateChains = (chain: ChainType): DataTemplate[] =>
    chain
      .reduce<ChainType>((result, value, ix, chain) => {
        if (value.type === "text") {
          return result.concat(value);
        }

        let input = [value] as ChainType;

        const before = ix === 0 ? undefined : result[result.length - 1];
        const after = ix + 1 === chain.length ? undefined : chain[ix + 1];

        if (before === undefined || before.type === "data") {
          input = [{ type: "text", value: "" }, value];
        }

        if (after === undefined) {
          input = [...input, { type: "text", value: "" }];
        }

        return result.concat(input);
      }, [])
      // Assign an id to each DataTemplate and a label to data-DataTemplates.
      .map(
        (d) =>
          ({
            ...d,
            ...(d.type === "data" ? { label: String(d.value.findLast((v) => v !== null)) } : {}),
            id: v4(),
          }) as DataTemplate,
      );

  return {
    ...templateExport,
    subcategory: restoreDataTemplateChains(templateExport.subcategory),
    content: restoreDataTemplateChains(templateExport.content),
    visibility: Visibility.visible,
    dataPointsCount: 0,
    updated: false,
  };
};

/**
 * It applies the existing templates from the found provider to the files of the export.
 * When a match is found, it generates a collection of WranglerItem objects.
 *
 * @param {Array<ProviderFile>} files
 * @param {Array<ExportType>} instructions
 * @returns {{ items: Array<{ item: WranglerItem; files: Array<ProviderFile> }>, others: Array<ProviderFile> }}
 *          An object containing two properties:
 *          - 'items': an array of objects, each containing a WranglerItem and an array of associated ProviderFiles,
 *          - 'others': an array of ProviderFiles that did not match any template.
 */
export function applyExistingTemplates(
  files: ProviderFile[],
  instructions: ExportType[],
): {
  items: { item: WranglerItem; files: ProviderFile[] }[];
  others: ProviderFile[];
} {
  let remainingFiles = files.filter((file) => file.compressedSize > 0);

  const wranglerItems = instructions.map<{
    item: WranglerItem;
    files: ProviderFile[];
  }>((item, ix) => {
    const itemFiles = remainingFiles.filter((file) =>
      isSimilarData(item.schema, file.parserData.schema ?? EMPTY_SCHEMA),
    );

    if (itemFiles.length === 0) {
      const remainingTemplates: DataPointTemplate[] = findDataPaths(item.schema)
        .filter((path) =>
          // Path is an array: `!==` compares references and is always true,
          // so every template was duplicated.
          item.templates.every((template) => !pathEquals(template.path, path)),
        )
        .map((path) =>
          createDataPointTemplate(
            path,
            extractFromTypeSchema(item.schema, path) ?? EMPTY_SCHEMA,
            Visibility.hidden,
            [],
          ),
        );

      return {
        item: {
          id: ix,
          exportId: -1,
          name: `unnamed item ${ix}`,
          schema: item.schema,
          category: undefined,
          templates: item.templates.map(restoreDataPointTemplate).concat(remainingTemplates),
          userStatus: UserInteractionStatus.Original,
          dataProgress: {
            didCompile: false,
            foundDataPoints: false,
            guessedCategory: false,
            hasTimestamp: false,
            isNotEmpty: false,
          },
          files: [] as ProviderFileRef[],
        },
        files: [] as ProviderFile[],
      };
    }

    const deserializedOutputs = deserializeOutputs(removeDuplicates(itemFiles.map((file) => file.providerId)));

    remainingFiles = remainingFiles.filter((file) => itemFiles.every(({ id }) => id !== file.id));

    const schema = itemFiles
      .slice(1)
      .reduce(
        (schema, file) => mergeUnionTypes(schema, file.parserData.schema ?? EMPTY_SCHEMA),
        itemFiles[0].parserData.schema ?? EMPTY_SCHEMA,
      );

    const remainingTemplates: DataPointTemplate[] = findDataPaths(schema)
      .filter((path) => item.templates.every((template) => !pathEquals(template.path, path)))
      .map((path) =>
        createDataPointTemplate(
          path,
          extractFromTypeSchema(schema, path) ?? EMPTY_SCHEMA,
          Visibility.hidden,
          itemFiles,
        ),
      );

    const wranglerItem: WranglerItem = {
      id: ix,
      exportId: -1,
      schema,
      name: commonName(itemFiles.map((file) => file.path)),
      category: undefined,
      templates: item.templates
        .map((template) => {
          const count = itemFiles.reduce((sum, file) => sum + countDataPoints(file, template.path), 0);

          return {
            ...restoreDataPointTemplate(template),
            dataPointsCount: count,
          };
        })
        .concat(remainingTemplates),
      userStatus: UserInteractionStatus.Original,

      dataProgress: {
        didCompile: false,
        foundDataPoints: false,
        guessedCategory: false,
        hasTimestamp: false,
        isNotEmpty: itemFiles.some((file) => (file.parserData.byteData?.length ?? 0) > 0),
      },
      files: itemFiles.map<ProviderFileRef>(({ id, exportId, providerId, path, hash }) => ({
        id,
        exportId,
        providerId,
        path,
        hash,
      })),
    };

    const compiledFiles = itemFiles.map((file) => compileFn(wranglerItem, file, deserializedOutputs));

    const itemCategory = compiledFiles
      .find((file) => file.data.some((point) => point.category !== undefined))
      ?.data.find((point) => point.category !== undefined)?.category;

    return {
      item: {
        ...wranglerItem,
        category: itemCategory,
        dataProgress: {
          ...wranglerItem.dataProgress,
          didCompile: compiledFiles.some((file) => file.data.length > 0),
          foundDataPoints: compiledFiles.some((file) => file.data.length > 0),
          guessedCategory: itemCategory !== undefined,
          hasTimestamp: item.templates.some((template) => template.selectedTimestamps.length > 0),
        },
      },
      files: compiledFiles,
    };
  });
  return {
    items: wranglerItems,
    others: remainingFiles,
  };
}
